//
// Copyright 2026 DXOS.org
//

import * as Array from 'effect/Array';
import * as Deferred from 'effect/Deferred';
import * as Duration from 'effect/Duration';
import * as Effect from 'effect/Effect';
import * as Fiber from 'effect/Fiber';
import * as FiberRef from 'effect/FiberRef';
import * as PubSub from 'effect/PubSub';
import * as Ref from 'effect/Ref';

import { Performance } from '@dxos/effect';
import { log } from '@dxos/log';

import { ActivationEvents } from '../../common';
import * as ActivationEvent from '../activation-event';
import * as Capability from '../capability';
import type * as CapabilityManager from '../capability-manager';
import { DependencyCycleError, DuplicateProviderError, MissingProviderError } from '../errors';
import type * as Plugin from '../plugin';
import * as ActivationGraph from './activation-graph';
import { whenIdle } from './idle';
import { type ManagerState } from './manager-state';
import { type ModuleLoader, together } from './module-loader';

/**
 * The activation lifecycle engine: owns {@link start} / {@link activate} /
 * {@link deactivatePlugin} / {@link resetEvent} and decides when each module's `activate`
 * runs. Every module names a wave via `activatesOn` (defaulting to Idle), and two paths
 * drive them:
 *
 * - {@link runDependencyPass}: rounds over the capability graph (vocabulary and ordering logic
 *   in `activation-graph.ts`), repeated until nothing new is runnable — each round's
 *   contributions (or a concurrent event wave's) can unlock modules that were waiting. A fired
 *   event stays fired, so a module whose wave has passed stays eligible in later rounds.
 * - Event dispatch: a module parks until its `activatesOn` fires, then runs as an event wave;
 *   providers of its requires are pulled on demand first, scoped to waves that have fired.
 *
 * Structural problems (cycles, duplicate or missing singleton providers) put the offending
 * plugins into an error state and exclude their modules; everything independent proceeds.
 */
export class ActivationScheduler {
  /** Per-wave activation concurrency cap — see the note at the `#executeWaves` call site. */
  static readonly WAVE_CONCURRENCY = 16;

  /** Events currently mid-activation (an `allOf` gate counts them as already fired). */
  readonly #activatingEvents = Effect.runSync(Ref.make<string[]>([]));
  /** Modules currently claimed by an event wave (excluded from re-matching). */
  readonly #activatingModules = Effect.runSync(Ref.make<string[]>([]));
  readonly #state: ManagerState;
  readonly #capabilities: CapabilityManager.CapabilityManager;
  readonly #loader: ModuleLoader;
  readonly #whenIdle: Effect.Effect<void>;

  constructor(
    state: ManagerState,
    capabilities: CapabilityManager.CapabilityManager,
    loader: ModuleLoader,
    whenIdleEffect: Effect.Effect<void> = whenIdle,
  ) {
    this.#state = state;
    this.#capabilities = capabilities;
    this.#loader = loader;
    this.#whenIdle = whenIdleEffect;
  }

  /**
   * Runs startup: the capability-dependency resolution pass for dependency-mode modules,
   * concurrently with the event dispatch for any module explicitly targeting the Startup
   * event. The event-level Startup `activated` message publishes only after both passes
   * complete. Idempotent — subsequent calls activate whatever registered since.
   *
   * Streaming: activation does NOT wait for the constructor's full enable chain. `started`
   * flips immediately and a pass runs over whatever has registered; each still-in-flight
   * `enable()` runs its own incremental pass on completion (see `PluginCatalog`), so early
   * definitions (e.g. the client) begin activating while later ones are still importing.
   * The ready signal below still waits for the complete set.
   */
  start(): Effect.Effect<boolean, Error, Plugin.Service> {
    return Effect.withFiberRuntime<boolean, Error, Plugin.Service>((fiber) =>
      this.#start(fiber).pipe(Effect.ensuring(this.#state.fibers.untrack(fiber))),
    );
  }

  #start(fiber: Fiber.Fiber<unknown, unknown>): Effect.Effect<boolean, Error, Plugin.Service> {
    return Effect.gen(this, function* () {
      if (yield* this.#state.isShuttingDown()) {
        log('skipping start during shutdown');
        return false;
      }

      // Tracked for the whole call: the startup wave runs in the early streaming pass as well as
      // the complete one, so shutdown must be able to interrupt either.
      yield* this.#state.fibers.track(fiber);

      // With streaming, activation work is spread across this call's passes AND the enable
      // chain's incremental passes — the return value reports whether anything activated.
      const activeBefore = this.#state.getActiveIds().length;

      yield* Ref.set(this.#state.started, true);
      // Early pass over already-registered modules. Failures here are re-evaluated by the
      // complete pass below, so they log rather than fail start.
      yield* this.runDependencyPass().pipe(
        Effect.catchAll((error) =>
          Effect.sync(() => log.warn('streaming startup pass failed', { error: String(error) })),
        ),
      );

      // The complete pass and the Startup event wait for the enable chain: the ready signal
      // must reflect the full registry (see the note in `activate`).
      yield* Deferred.await(this.#state.initialized);

      const key = ActivationEvent.eventKey(ActivationEvent.Startup);

      // A capability cycle spanning event boundaries would leave both chains pending
      // forever. Surface the lock (error state on the involved plugins) and continue —
      // the cycle members simply never activate; everything else proceeds.
      yield* this.#reportGlobalCycle();

      // The startup wave and the dependency pass are the same thing: the pass already admits every
      // module whose wave is firing, so dispatching the event alongside it would claim the same
      // modules twice. An undeclared `activatesOn` normalizes to Idle, so what runs here is the
      // modules that asked for Startup plus the baseline providers they pull.
      this.#state.clearPendingReset(key);
      // Graph-level failures (missing provider, duplicate provider, cycle) fail the start call;
      // publish them so boot UIs surface the root cause instead of a silent hang behind their
      // own watchdog.
      const ranAny = yield* this.runDependencyPass().pipe(
        Effect.tapError((error) =>
          Effect.gen(this, function* () {
            log.error('dependency activation failed', { error: String(error) });
            yield* PubSub.publish(this.#state.activation, { event: key, state: 'error', error });
          }),
        ),
      );

      // The enable chain forked its incremental passes, so loads may still be in flight outside
      // the passes above. Ready must reflect a settled world — alternate between awaiting
      // in-flight loads and mop-up passes until neither does any work.
      for (;;) {
        const settledAny = yield* this.#loader.awaitAllSettled();
        const ranAny = yield* this.runDependencyPass().pipe(
          Effect.catchAll((error) =>
            Effect.sync(() => {
              log.warn('startup mop-up pass failed', { error: String(error) });
              return false;
            }),
          ),
        );
        if (!settledAny && !ranAny) {
          break;
        }
      }

      // The event-level Startup `activated` message (no `module` field) is the app-ready
      // signal (see useApp); it must not publish before the startup wave finishes.
      this.#state.markEventFired(key);
      yield* PubSub.publish(this.#state.activation, { event: key, state: 'activated' });

      // The idle wave belongs to the manager rather than to each host: it is an app-framework
      // event, and a host that owned the wait would re-implement it (and could forget to).
      // Forked so `start` returns at ready, and deferred to host idle so the registration
      // contributions gated on it land after first paint instead of competing with it.
      // Tracked: the wave sits behind a wait of up to 15s, and an untracked daemon outlives
      // `shutdown()` — dispatching Idle into a manager that has already reset re-activates
      // modules after teardown.
      yield* this.#state.fibers.trackForked(yield* this.#activateWhenIdle().pipe(Effect.forkDaemon));

      return this.#state.getActiveIds().length > activeBefore || ranAny;
    });
  }

  /**
   * Fires the idle wave once the host goes idle. Failures are logged rather than propagated:
   * the wave carries registration contributions, so one broken plugin must not take down an
   * app that is already interactive.
   */
  #activateWhenIdle(): Effect.Effect<void, never, Plugin.Service> {
    return Effect.gen(this, function* () {
      yield* this.#whenIdle;
      yield* this.activate(ActivationEvents.Idle).pipe(
        Effect.catchAll((error) => Effect.sync(() => log.warn('idle activation failed', { error: String(error) }))),
      );
    });
  }

  /**
   * Runs an activation event end to end. Startup delegates to {@link start}; anything else
   * waits for the manager's initialization, then dispatches the event.
   */
  activate(
    event: ActivationEvent.ActivationEvent | string,
    params?: { before?: string; after?: string },
  ): Effect.Effect<boolean, Error, Plugin.Service> {
    const key = typeof event === 'string' ? event : ActivationEvent.eventKey(event);
    return Effect.gen(this, function* () {
      // Startup is not a plain event: it triggers the dependency pass alongside the event
      // dispatch. Delegating keeps useApp/harness/cli call sites unchanged.
      if (key === ActivationEvent.eventKey(ActivationEvent.Startup)) {
        return yield* this.start();
      }

      if (yield* this.#state.isShuttingDown()) {
        log('skipping activation during shutdown', { key, ...params });
        return false;
      }

      // Re-dispatching a fired event is already a no-op — an active module is never re-selected —
      // but the instrumentation below is not free (two marks, a devtools track entry, a forked
      // warning timer), and demand events are fired from reactive paths that re-evaluate freely.
      // Bail before paying for a dispatch that has nothing to activate. The module scan is still
      // required: modules register after the fact under streaming start, so a fired event can
      // have newly-eligible modules. A fired event implies the wait below already completed once,
      // so the scan is safe this early.
      if (this.#state.eventFired(key)) {
        const activatingEvents = yield* this.#activatingEvents;
        const activatingModules = yield* this.#activatingModules;
        if (this.#getModulesForActivation(key, activatingEvents, activatingModules).length === 0) {
          log('no modules to activate', { key, ...params });
          return false;
        }
      }

      // Dispatch-latency split: `requested` → `initialized` is registration wait; `initialized`
      // → the wave's `event:` measure is provider-join wait (in-flight round-1 loads).
      performance.mark(`milestone:dispatch:${key}:requested`);

      // Wait for the constructor's core/enabled `enable()` chain — including
      // any async dynamic imports for lazy plugins — to finish registering
      // modules. Without this, dispatching to an empty module set is the
      // observable symptom of the race.
      yield* Deferred.await(this.#state.initialized);
      performance.mark(`milestone:dispatch:${key}:initialized`);

      return yield* Effect.withFiberRuntime<boolean, Error, Plugin.Service>((fiber) =>
        this.#activateEvent(key, params, fiber).pipe(
          together(
            Effect.sleep(Duration.seconds(15)).pipe(
              Effect.andThen(Effect.sync(() => log.warn('event activation is taking a long time', { event: key }))),
            ),
          ),
          Performance.addTrackEntry({
            name: key,
            devtools: {
              dataType: 'track-entry',
              track: 'Event Activation',
              trackGroup: 'Composer',
              color: 'primary',
            },
          }),
        ),
      );
    });
  }

  /**
   * Deactivates all of a plugin's modules. Active modules elsewhere that require a singleton
   * capability provided by this plugin are deactivated first (reverse activation order) and
   * marked for reactivation when a provider returns.
   */
  deactivatePlugin(id: string): Effect.Effect<boolean, Error> {
    return Effect.gen(this, function* () {
      const plugin = this.#state.getPlugin(id);
      if (!plugin) {
        return false;
      }

      const modules = plugin.modules;

      const dependents = this.#collectCapabilityDependents(modules);
      for (const dependent of dependents) {
        yield* this.#loader.deactivate(dependent);
        this.#state.reactivateOnNextPass.add(dependent.id);
      }

      const results = yield* Effect.all(
        modules.map((module) => this.#loader.deactivate(module)),
        { concurrency: 'unbounded' },
      );
      return results.every((result) => result);
    });
  }

  /**
   * Re-activates the modules that were activated by the event.
   */
  resetEvent(event: ActivationEvent.ActivationEvent | string): Effect.Effect<boolean, Error, Plugin.Service> {
    return Effect.gen(this, function* () {
      const key = typeof event === 'string' ? event : ActivationEvent.eventKey(event);
      log('reset', { key });
      const modules = this.#state.getActiveModulesByEvent(key);
      const results = yield* Effect.all(
        modules.map((module) => this.#loader.deactivate(module)),
        { concurrency: 'unbounded' },
      );

      if (results.every((result) => result)) {
        return yield* this.activate(key);
      } else {
        return false;
      }
    });
  }

  /** Shutdown support: forget in-flight event/module claims. */
  clearClaims(): Effect.Effect<void> {
    return Effect.all([Ref.set(this.#activatingEvents, []), Ref.set(this.#activatingModules, [])]).pipe(Effect.asVoid);
  }

  /**
   * Runs one activation event: matches parked event-mode modules (an `allOf` module joins once
   * all its events have fired), pulls their inactive dependency providers on demand, activates
   * them, then re-runs the dependency pass so contributions made here can unlock modules that
   * were waiting.
   */
  #activateEvent(
    key: string,
    params: { before?: string; after?: string } | undefined,
    fiber: Fiber.Fiber<unknown, unknown>,
    opts?: { suppressEventMessage?: boolean },
  ): Effect.Effect<boolean, Error, Plugin.Service> {
    return Effect.gen(this, function* () {
      yield* this.#state.fibers.track(fiber);
      log('activating', { key, ...params });
      yield* Ref.update(this.#activatingEvents, (activating) => Array.append(activating, key));
      this.#state.clearPendingReset(key);

      const activatingEvents = yield* this.#activatingEvents;
      const activatingModules = yield* this.#activatingModules;
      const modules = this.#getModulesForActivation(key, activatingEvents, activatingModules);
      if (modules.length === 0) {
        log('no modules to activate', { key });
        this.#state.markEventFired(key);
        return false;
      }

      // Modules resolve their requires on demand: inactive providers of unsatisfied singleton
      // requires are activated first (transitively).
      yield* this.#pullDependencyProviders(modules, key);

      const activated = yield* this.#activateModulesForEvent(key, modules, opts);

      // Follow-up pass: contributions made by this event may unlock modules that were waiting
      // on them. Cheap no-op when nothing became runnable.
      if (yield* this.#state.isStarted()) {
        yield* this.runDependencyPass({});
      }

      return activated;
    }).pipe(
      Effect.ensuring(
        Effect.all([
          this.#state.fibers.untrack(fiber),
          Ref.update(this.#activatingEvents, (activating) => Array.filter(activating, (event) => event !== key)),
        ]),
      ),
    );
  }

  /**
   * Activates inactive modules in dependency order of the capability graph, in rounds,
   * repeated until a round activates nothing new. Candidates are dependency-mode modules plus
   * event-mode modules whose `activatesOn` events have already fired; modules whose singleton
   * requires cannot be satisfied by this pass WAIT — they are reconsidered by the next round
   * (each round of contributions, and each event wave, can unlock them). With
   * `candidateModules`, the first round is scoped to those modules (plus any marked for
   * reactivation); follow-up rounds consider the full pool unless `scopedToCandidates` keeps
   * them scoped — demand pulls and event waves must not become drain workers for the whole
   * startup pool (that serializes event dispatch behind unrelated startup work).
   *
   * Structural problems — duplicate singleton providers within a round, capability cycles,
   * or a singleton requirement no registered module of any mode could ever provide — put
   * the offending plugins into an error state (the `failed` atom, plus an error activation
   * message) and the pass continues with everything else. Individual module activation
   * failures are recorded per plugin (via the loader) and skip that module's transitive
   * dependents without aborting independent modules.
   */
  runDependencyPass(options?: {
    candidateModules?: Plugin.PluginModule[];
    scopedToCandidates?: boolean;
  }): Effect.Effect<boolean, Error, Plugin.Service> {
    return Effect.gen(this, function* () {
      let scoped = options?.candidateModules;
      let ranAny = false;
      let allSucceeded = true;
      // Contributions made by one round (or concurrently by event waves) can make modules that
      // were waiting runnable, so keep running rounds until one activates nothing. Each round
      // activates at least one module or ends the loop, so this terminates.
      for (;;) {
        const round = yield* this.#runRound(scoped);
        if (round === undefined) {
          break;
        }
        ranAny = true;
        allSucceeded = allSucceeded && round;
        // Follow-up rounds consider everything that might have been unlocked — unless scoped:
        // a demand pull draining the full pool would block its event wave on unrelated work.
        scoped = options?.scopedToCandidates ? options?.candidateModules : undefined;
      }
      return ranAny && allSucceeded;
    });
  }

  /**
   * Detects capability cycles across the FULL module set, regardless of event gating.
   * Two modules on different activation events that require each other's provides would
   * otherwise wait forever (neither chain can start); this surfaces the lock at startup:
   * the members are put into an error state and excluded — they simply never activate,
   * and everything else proceeds.
   */
  #reportGlobalCycle(): Effect.Effect<void> {
    return Effect.gen(this, function* () {
      const graph = ActivationGraph.buildSingletonGraph(this.#state.getModules());
      if (ActivationGraph.computeActivationWaves(graph, ['hard']) !== undefined) {
        return;
      }
      const path = ActivationGraph.findCyclePath(graph, ['hard']);
      yield* this.#reportStructuralError(
        path.map((entry) => entry.module),
        new DependencyCycleError({ path }),
      );
      path.forEach((entry) => this.#state.structurallyFailed.add(entry.module));
    });
  }

  /**
   * Collects active modules (outside the given set) whose declared requires include a
   * singleton capability provided by the given modules, transitively. Returned in reverse
   * activation order, which is reverse topological order (safe deactivation order).
   */
  #collectCapabilityDependents(modules: readonly Plugin.PluginModule[]): Plugin.PluginModule[] {
    const active = this.#state.getActiveIds();
    const allModules = this.#state.getModules();
    const ownIds = new Set(modules.map((module) => module.id));
    const providedIds = new Set<string>();
    const collectProvides = (module: Plugin.PluginModule) => {
      for (const capability of module.activation.provides) {
        if (capability.arity === 'single') {
          providedIds.add(capability.identifier);
        }
      }
    };
    modules.filter((module) => active.includes(module.id)).forEach(collectProvides);

    const dependents = new Map<string, Plugin.PluginModule>();
    let changed = providedIds.size > 0;
    while (changed) {
      changed = false;
      for (const module of allModules) {
        if (ownIds.has(module.id) || dependents.has(module.id) || !active.includes(module.id)) {
          continue;
        }
        if (module.activation.requires.some((capability) => providedIds.has(capability.identifier))) {
          dependents.set(module.id, module);
          collectProvides(module);
          changed = true;
        }
      }
    }

    const order = this.#state.getActiveIds();
    return [...dependents.values()].sort((a, b) => order.indexOf(b.id) - order.indexOf(a.id));
  }

  #activateModulesForEvent(
    key: string,
    modules: Plugin.PluginModule[],
    opts?: { suppressEventMessage?: boolean },
  ): Effect.Effect<boolean, Error, Plugin.Service> {
    const activatingModuleIds = modules.map((module) => module.id);
    return Effect.gen(this, function* () {
      yield* Ref.update(this.#activatingModules, (activating) => Array.appendAll(activating, activatingModuleIds));

      log('activation wave', { event: key, modules: activatingModuleIds });
      performance.mark(`event:${key}:start`);
      yield* PubSub.publish(this.#state.activation, { event: key, state: 'activating' });

      // Same-event provider/consumer pairs are topologically ordered with per-module
      // contribution via the capability-graph machinery. Scoped: the post-wave pass in
      // `#activateEvent` handles unlocking, so the wave never drains the startup pool.
      yield* this.runDependencyPass({ candidateModules: modules, scopedToCandidates: true });

      // A matched module may be mid-load via a concurrent wave (the round filters it as
      // `isLoading` to avoid in-round deadlocks). Await those loads before reporting the event
      // activated — demand pulls rely on "activate resolved ⇒ matched modules contributed"
      // (a lookup retry that runs earlier misses the handlers and fails a one-shot invocation).
      // Bounded: every load settles its deferred under the activation timeout.
      const inFlight = (yield* this.#notActivatingHere(modules)).filter((module) => this.#loader.isLoading(module.id));
      if (inFlight.length > 0) {
        yield* Effect.all(
          inFlight.map((module) => this.#loader.awaitSettled(module.id)),
          { concurrency: 'unbounded', discard: true },
        );
      }

      this.#state.markEventFired(key);

      performance.mark(`event:${key}:end`);
      performance.measure(`event:${key}`, `event:${key}:start`, `event:${key}:end`);
      // `start()` suppresses the event-level message for Startup and publishes it itself
      // once the concurrent dependency pass has also completed (the useApp ready gate).
      if (!opts?.suppressEventMessage) {
        yield* PubSub.publish(this.#state.activation, { event: key, state: 'activated' });
      }
      log('activated', { key });

      return true;
    }).pipe(
      Effect.ensuring(
        Ref.update(this.#activatingModules, (activating) =>
          Array.filter(activating, (module) => !activatingModuleIds.includes(module)),
        ),
      ),
    );
  }

  #getModulesForActivation(
    key: string,
    activatingEvents: string[],
    activatingModules: string[],
  ): Plugin.PluginModule[] {
    return this.#state.getInactiveModulesByEvent(key).filter((module) => {
      const spec = module.activation;
      const allOf = ActivationEvent.isAllOf(spec.activatesOn);
      if (!allOf) {
        return true;
      }

      // Check to see if all of the events in the `allOf` have been fired.
      // An event can be considered "fired" if it is in the `eventsFired` list or if it is currently being activated.
      const events = ActivationEvent.getEvents(spec.activatesOn).filter(
        (event) => ActivationEvent.eventKey(event) !== key,
      );
      return (
        events.every(
          (event) =>
            this.#state.eventFired(ActivationEvent.eventKey(event)) ||
            activatingEvents.includes(ActivationEvent.eventKey(event)),
        ) && !activatingModules.includes(module.id)
      );
    });
  }

  /**
   * Records a structural dependency-graph error against a plugin and publishes it on the
   * activation stream (so boot UIs surface it) without aborting the pass.
   */
  #reportStructuralError(moduleIds: string[], error: Error): Effect.Effect<void> {
    return Effect.gen(this, function* () {
      log.error('dependency graph error', { error: String(error), modules: moduleIds });
      const plugins = new Set<string>();
      for (const moduleId of moduleIds) {
        const pluginId = this.#state.pluginIdOfModule(moduleId);
        if (pluginId !== undefined) {
          plugins.add(pluginId);
        }
      }
      plugins.forEach((pluginId) => this.#state.recordFailure(pluginId, 'activation', error));
      yield* PubSub.publish(this.#state.activation, {
        event: ActivationEvent.eventKey(ActivationEvent.Startup),
        state: 'error',
        error,
      });
    });
  }

  /**
   * Whether a module's wave has fired: every event for an `all-of` declaration, any one for a
   * plain or `one-of` declaration. A fired event stays fired, so this stays true for the rest of
   * the session once the module's wave has passed.
   *
   * `activating` is the wave currently being dispatched, counted as fired: an event is marked
   * fired only once its modules have settled, so without this the wave's own modules would be
   * excluded from the very round activating them.
   */
  #waveFired(module: Plugin.PluginModule, activating?: string): boolean {
    const { activatesOn } = module.activation;
    const events = ActivationEvent.getEvents(activatesOn).map(ActivationEvent.eventKey);
    const fired = (event: string) => event === activating || this.#state.eventFired(event);
    return ActivationEvent.isAllOf(activatesOn) ? events.every(fired) : events.some(fired);
  }

  /**
   * Whether a module can activate now: its wave has already fired, or it declared Startup. The
   * catalog uses this to pick which of a newly enabled plugin's modules join an incremental pass;
   * the rest wait for their event.
   *
   * Deliberately NOT `#isBaselineWave`, which also admits Idle. Candidates handed to
   * `runDependencyPass` are EXPLICIT candidates and bypass the `#waveFired` gate in `#runRound`, so
   * admitting idle-default modules here would activate them eagerly during boot for any plugin
   * whose `enable()` lands after `started` flips — the dominant path in Composer, where lazy plugin
   * stubs enable over seconds while Startup dispatches milliseconds after mount. That would make
   * deferral depend on an enable/start race and defeat the idle default entirely. Idle stays in the
   * baseline for PROVIDER PULLS only (see {@link ActivationScheduler.#isBaselineWave}).
   */
  isEligible(module: Plugin.PluginModule): boolean {
    return this.#waveFired(module) || this.#declaresStartup(module);
  }

  /** Whether the module explicitly names the Startup wave. */
  #declaresStartup(module: Plugin.PluginModule): boolean {
    const startup = ActivationEvent.eventKey(ActivationEvent.Startup);
    return ActivationEvent.getEvents(module.activation.activatesOn).map(ActivationEvent.eventKey).includes(startup);
  }

  /**
   * Whether a module sits in the baseline wave — `Startup u Idle`. A module there is pullable as a
   * provider by any wave, whether or not its own wave has run; explicitly gated providers stay
   * wave-scoped, which is what keeps two mutually-exclusive providers of one capability from both
   * being candidates.
   *
   * Idle is in the baseline because it is what omitting `activatesOn` normalizes to: scoping
   * un-annotated modules to their own wave would make every un-annotated provider invisible to the
   * startup pass, so `ClientPlugin`'s `Client` would not initialize until idle. Modules declaring
   * `Startup` explicitly lose nothing — their wave fires first, so `#waveFired` covers them after.
   */
  #isBaselineWave(module: Plugin.PluginModule): boolean {
    const baseline = [ActivationEvent.Startup, ActivationEvent.Idle].map(ActivationEvent.eventKey);
    return ActivationEvent.getEvents(module.activation.activatesOn)
      .map(ActivationEvent.eventKey)
      .some((event) => baseline.includes(event));
  }

  /**
   * Drops the modules whose `activate` bodies this fiber is running inside. A body that fires an
   * activation event is a caller of the wave, so awaiting its own load is a circular wait that
   * ends only at the activation timeout — recorded as a `PluginTimeoutError` and read by the
   * failure supervisor as a broken plugin, which then disables it. Their contributions are simply
   * not visible to this wave; the body contributes them when it returns.
   */
  #notActivatingHere(modules: Plugin.PluginModule[]): Effect.Effect<Plugin.PluginModule[]> {
    return Effect.gen(function* () {
      const activating = yield* FiberRef.get(Capability.ActivatingModuleIds);
      if (activating.size === 0) {
        return modules;
      }
      return modules.filter((module) => !activating.has(module.id));
    });
  }

  /**
   * One topological activation round. Returns `undefined` when nothing is runnable.
   */
  #runRound(
    candidateModules: Plugin.PluginModule[] | undefined,
  ): Effect.Effect<boolean | undefined, Error, Plugin.Service> {
    return Effect.gen(this, function* () {
      const key = ActivationEvent.eventKey(ActivationEvent.Startup);
      const active = this.#state.getActiveIds();
      const allModules = this.#state.getModules();

      // During the streaming bootstrap the provider's plugin definition may simply not have
      // registered yet — the module waits and the post-initialization pass re-evaluates;
      // a missing provider is structural only once the registry is complete.
      const registryComplete = yield* Deferred.isDone(this.#state.initialized);

      const reactivations = allModules.filter((module) => this.#state.reactivateOnNextPass.has(module.id));
      // Explicitly passed candidates are trusted to be triggered (an event wave passes its
      // matched modules before the event is marked fired); pooled candidates must have their
      // events already fired.
      const explicit = new Set((candidateModules ?? []).map((module) => module.id));
      const pool = candidateModules ? [...candidateModules, ...reactivations] : allModules;
      const seen = new Set<string>();
      const candidates = pool.filter((module) => {
        if (
          active.includes(module.id) ||
          seen.has(module.id) ||
          this.#state.structurallyFailed.has(module.id) ||
          // Already loading via another path (memoized): awaiting it here could deadlock —
          // e.g. a cascade triggered by an event wave that an in-flight module itself fired.
          this.#loader.isLoading(module.id)
        ) {
          return false;
        }
        // A multi require is a collection, so "satisfied" is only meaningful once every plugin
        // definition has registered — a consumer admitted during the streaming window snapshots
        // whatever happens to have contributed so far (the process manager's one-shot
        // `Capabilities.LayerSpec` read is how this surfaced). Which providers are still to come
        // is unknowable mid-registration, so these wait for the complete pass wholesale; this is
        // the ordering they had before streaming.
        if (!registryComplete && module.activation.requires.some((capability) => capability.arity === 'multi')) {
          return false;
        }
        if (!explicit.has(module.id) && !this.#waveFired(module, key)) {
          return false;
        }
        seen.add(module.id);
        return true;
      });
      if (candidates.length === 0) {
        return undefined;
      }

      // Singleton provider index across candidates and already-active typed modules.
      // The duplicate check spans only modules in play, so mutually-exclusive event-gated
      // alternatives (only one has fired) do not trip it. Duplicates put both providers into
      // an error state and exclude them; their dependents wait.
      const providerIndex = new Map<string, string>();
      const structurallyExcluded = new Set<string>();
      const activeTypedModules = allModules.filter((module) => active.includes(module.id));
      for (const module of [...activeTypedModules, ...candidates]) {
        for (const capability of module.activation.provides) {
          if (capability.arity !== 'single') {
            continue;
          }
          const existing = providerIndex.get(capability.identifier);
          if (existing !== undefined && existing !== module.id) {
            const error = new DuplicateProviderError({
              capability: capability.identifier,
              providers: [existing, module.id],
            });
            yield* this.#reportStructuralError([existing, module.id], error);
            structurallyExcluded.add(existing);
            structurallyExcluded.add(module.id);
            this.#state.structurallyFailed.add(existing);
            this.#state.structurallyFailed.add(module.id);
            providerIndex.delete(capability.identifier);
            continue;
          }
          providerIndex.set(capability.identifier, module.id);
        }
      }

      // Does ANY registered module (active or not, fired or not) provide this?
      const anyRegisteredProvider = (identifier: string): boolean =>
        allModules.some((module) => module.activation.provides.some((provided) => provided.identifier === identifier));

      // Pure selection: everything that waits here is reconsidered by a later round; requires
      // with no possible provider at all are a configuration error recorded against the
      // requiring plugin.
      const { runnable, missing, waiting } = ActivationGraph.selectRunnableModules({
        candidates,
        excluded: structurallyExcluded,
        isSatisfied: (capability) => this.#capabilities.getAll(capability).length > 0,
        providerOf: (identifier) => providerIndex.get(identifier),
        activeIds: [...active],
        anyRegisteredProvider,
      });
      for (const waits of waiting) {
        log('module waiting on capability', { module: waits.module.id, capability: waits.capability });
      }
      for (const miss of missing) {
        if (!registryComplete) {
          log('module waiting on unregistered provider', { module: miss.module.id, capability: miss.capability });
          continue;
        }
        yield* this.#reportStructuralError(
          [miss.module.id],
          new MissingProviderError({
            capability: miss.capability,
            requiredBy: [miss.module.id],
            registered: this.#capabilities.listRegisteredIdentifiers(),
          }),
        );
        this.#state.structurallyFailed.add(miss.module.id);
      }
      if (runnable.length === 0) {
        return undefined;
      }
      runnable.forEach((module) => this.#state.reactivateOnNextPass.delete(module.id));

      const graph = ActivationGraph.buildRoundGraph(runnable, {
        isSatisfied: (capability) => this.#capabilities.getAll(capability).length > 0,
        providerOf: (identifier) => providerIndex.get(identifier),
      });

      const ordered = yield* this.#orderRoundBreakingCycles(graph);
      if (ordered === undefined) {
        return undefined;
      }
      const { waves, cycleFailed } = ordered;
      log('dependency activation waves', {
        waves: waves.map((wave) => wave.map((module) => module.id)),
      });

      return yield* this.#executeWaves(waves, graph, cycleFailed, key);
    });
  }

  /**
   * Orders a round's graph into activation waves (see `activation-graph.ts` for the terms),
   * repeatedly removing required-edge cycles (recorded as an error state on the involved
   * plugins; their dependents skip via the failed set) until the remaining graph is acyclic.
   * Best-effort ordering edges are then layered on top: if they cycle, the round falls back to
   * required-edge order wholesale. Returns `undefined` when nothing survives cycle removal.
   */
  #orderRoundBreakingCycles(
    graph: ActivationGraph.ActivationGraphModel,
  ): Effect.Effect<{ waves: Plugin.PluginModule[][]; cycleFailed: Set<string> } | undefined, Error> {
    return Effect.gen(this, function* () {
      const cycleFailed = new Set<string>();
      let hardWaves = ActivationGraph.computeActivationWaves(graph, ['hard']);
      while (hardWaves === undefined) {
        const path = ActivationGraph.findCyclePath(graph, ['hard']);
        yield* this.#reportStructuralError(
          path.map((entry) => entry.module),
          new DependencyCycleError({ path }),
        );
        const members = path.map((entry) => entry.module);
        members.forEach((member) => {
          cycleFailed.add(member);
          this.#state.structurallyFailed.add(member);
        });
        graph.removeNodes(members);
        if (graph.nodes.length === 0) {
          return undefined;
        }
        hardWaves = ActivationGraph.computeActivationWaves(graph, ['hard']);
      }

      const combinedWaves = ActivationGraph.computeActivationWaves(graph, ['hard', 'soft']);
      if (combinedWaves === undefined && ActivationGraph.hasSoftEdges(graph)) {
        log('multi-capability soft ordering dropped (cycle)', { modules: graph.nodes.map((node) => node.id) });
      }
      return { waves: combinedWaves ?? hardWaves, cycleFailed };
    });
  }

  /**
   * Activates a round wave by wave (modules within a wave run concurrently). A module whose
   * required provider failed is skipped and marked failed so its own dependents skip too;
   * independent modules proceed. Cycle members arrive pre-failed so their dependents skip as
   * well. Returns whether every module in the round activated.
   */
  #executeWaves(
    waves: Plugin.PluginModule[][],
    graph: ActivationGraph.ActivationGraphModel,
    preFailed: Set<string>,
    key: string,
  ): Effect.Effect<boolean, Error, Plugin.Service> {
    return Effect.gen(this, function* () {
      const failed = new Set<string>(preFailed);
      const providersOf = (moduleId: string): string[] => ActivationGraph.requiredProviderIds(graph, moduleId);
      // Singleton providers gate dependents (in this round or a later pass), so under the
      // concurrency cap they claim the first slots instead of queuing behind leaf modules.
      const singletonProvides = (module: Plugin.PluginModule): number =>
        module.activation.provides.filter((capability) => capability.arity === 'single').length;
      let allSucceeded = true;
      for (const wave of waves) {
        const ordered = [...wave].sort((a, b) => singletonProvides(b) - singletonProvides(a));
        yield* Effect.all(
          ordered.map((module) =>
            Effect.gen(this, function* () {
              if (providersOf(module.id).some((provider) => failed.has(provider))) {
                log.warn('skipping module: provider failed', { module: module.id });
                failed.add(module.id);
                allSucceeded = false;
                return;
              }
              const result = yield* this.#activateModule(module, key).pipe(Effect.either);
              if (result._tag === 'Left') {
                failed.add(module.id);
                allSucceeded = false;
              }
            }),
          ),
          // Bounded: a 350-wide unbounded fan-out oversubscribes import/parse and stretches
          // every module's wall clock (the measured contention plateau); small waves and
          // tests (which gate concurrent modules on each other) stay fully concurrent.
          { concurrency: ActivationScheduler.WAVE_CONCURRENCY },
        );
      }
      return allSucceeded;
    });
  }

  /**
   * Loads and contributes a single dependency-mode module. Contribution happens as the module
   * completes (not batched per wave), so singleton gating is exactly wave ordering.
   */
  #activateModule(module: Plugin.PluginModule, parentEvent: string): Effect.Effect<void, Error, Plugin.Service> {
    return Effect.gen(this, function* () {
      if (this.#state.getActiveIds().includes(module.id)) {
        return;
      }
      const capabilities = yield* this.#loader.load(module, parentEvent);
      yield* this.#loader.contribute(module, capabilities);
    });
  }

  /**
   * Activates the inactive dependency-mode providers (transitively) of the given modules'
   * unsatisfied singleton requires, plus any inactive dependency-mode providers of their
   * multi requires. Used before running event-mode modules so their requires resolve on
   * demand.
   *
   * Multi requires never gate (they resolve to whatever is currently contributed), but a
   * pulled provider that takes a one-shot snapshot of a multi capability (e.g. the process
   * manager's `Capabilities.LayerSpec` collection) needs its fellow multi providers activated
   * in the *same* scoped round so the round's ordering can land them first — otherwise a
   * narrow pull (this provider alone) skips that ordering entirely and the snapshot can be
   * taken before sibling providers have contributed.
   */
  #pullDependencyProviders(
    modules: Plugin.PluginModule[],
    activating?: string,
  ): Effect.Effect<void, Error, Plugin.Service> {
    return Effect.gen(this, function* () {
      const active = this.#state.getActiveIds();
      const allModules = this.#state.getModules();
      const providerIndex = new Map<string, Plugin.PluginModule>();
      const multiProviderIndex = new Map<string, Plugin.PluginModule[]>();
      for (const module of allModules) {
        // Wave-scoped, with the baseline wave always in scope: an explicitly gated provider is
        // pullable only once its own wave has fired, so two providers of the same singleton
        // capability gated on mutually exclusive events are never both candidates.
        if ((!this.#waveFired(module, activating) && !this.#isBaselineWave(module)) || active.includes(module.id)) {
          continue;
        }
        for (const capability of module.activation.provides) {
          if (capability.arity === 'single') {
            if (!providerIndex.has(capability.identifier)) {
              providerIndex.set(capability.identifier, module);
            }
          } else {
            const providers = multiProviderIndex.get(capability.identifier) ?? [];
            providers.push(module);
            multiProviderIndex.set(capability.identifier, providers);
          }
        }
      }
      if (providerIndex.size === 0 && multiProviderIndex.size === 0) {
        return;
      }

      const needed = new Map<string, Plugin.PluginModule>();
      const visit = (requires: readonly Capability.AnyTag[]) => {
        for (const capability of requires) {
          if (capability.arity === 'multi') {
            for (const provider of multiProviderIndex.get(capability.identifier) ?? []) {
              if (!needed.has(provider.id)) {
                needed.set(provider.id, provider);
                visit(provider.activation.requires);
              }
            }
            continue;
          }
          if (this.#capabilities.getAll(capability).length > 0) {
            continue;
          }
          const provider = providerIndex.get(capability.identifier);
          if (provider && !needed.has(provider.id)) {
            needed.set(provider.id, provider);
            visit(provider.activation.requires);
          }
        }
      };
      for (const module of modules) {
        visit(module.activation.requires);
      }

      if (needed.size > 0) {
        log('pulling dependency providers', { modules: [...needed.keys()] });
        // Providers already mid-load (e.g. via the concurrent startup pass) are joined, not
        // re-drained — the scoped round below filters them as `isLoading` and would otherwise
        // return before their capabilities land.
        const inFlight = (yield* this.#notActivatingHere([...needed.values()])).filter((module) =>
          this.#loader.isLoading(module.id),
        );
        if (inFlight.length > 0) {
          yield* Effect.all(
            inFlight.map((module) => this.#loader.awaitSettled(module.id)),
            { concurrency: 'unbounded', discard: true },
          );
        }
        yield* this.runDependencyPass({ candidateModules: [...needed.values()], scopedToCandidates: true });
      }
    });
  }
}
